/**
 * Loads the Velt script dynamically and calls the callback when ready.
 * Returns a cleanup function to remove the event listener if component unmounts.
 *
 * ## Issue (Rapid Mount/Unmount with Slow Network)
 *
 * When VeltProvider is rapidly mounted/unmounted (e.g., conditional rendering,
 * React Strict Mode, or route changes) while the Velt script is still loading
 * over a slow network, multiple issues can occur:
 *
 * 1. First component mount creates the script tag and starts loading
 * 2. Component unmounts before script loads (script tag remains in DOM)
 * 3. Second component mount finds existing script tag
 * 4. OLD BEHAVIOR: Immediately triggered callback assuming script was loaded
 * 5. PROBLEM: Script wasn't actually loaded yet (still loading due to network delay)
 * 6. This caused initVelt to run with window.Velt = undefined
 *
 * ## Fix
 *
 * 1. Check if window.Velt exists before triggering callback for existing scripts
 * 2. If script tag exists but window.Velt doesn't, attach a new load event listener
 * 3. Return a cleanup function that removes the event listener when component unmounts
 * 4. This prevents orphaned callbacks from firing on destroyed component instances
 *
 * @returns Cleanup function to remove event listener (call on component unmount)
 */
var loadVelt = function (callback, version, staging, develop, proxyDomain, integrity, integrityValue) {
    if (version === void 0) { version = 'latest'; }
    if (staging === void 0) { staging = false; }
    if (develop === void 0) { develop = false; }
    var existingScript = document.getElementById('veltScript');
    // Store reference to the handler so we can remove it on cleanup.
    // This is essential for proper cleanup - we need the exact same function
    // reference to remove the event listener later.
    var loadHandler = null;
    var scriptElement = null;
    if (!existingScript) {
        // No script tag exists - this is the first component to request Velt
        var script = document.createElement('script');
        if (staging) {
            script.src = "https://serveprivatenpmpackage-4mfhcuyw2q-uc.a.run.app/sdk-staging/lib/sdk@".concat(version, "/velt.js");
        }
        else if (develop) {
            script.src = "https://serveprivatenpmpackage-4mfhcuyw2q-uc.a.run.app/sdk-dev/lib/sdk@".concat(version, "/velt.js");
        }
        else {
            if (proxyDomain) {
                // remove trailing slash from proxy
                if (proxyDomain[proxyDomain.length - 1] === '/') {
                    proxyDomain = proxyDomain.slice(0, -1);
                }
                script.src = "".concat(proxyDomain, "/lib/sdk@").concat(version, "/velt.js");
            }
            else {
                script.src = "https://cdn.velt.dev/lib/sdk@".concat(version, "/velt.js");
            }
        }
        script.id = 'veltScript';
        script.type = 'module';
        if (integrity && integrityValue) {
            script.integrity = integrityValue;
            script.crossOrigin = 'anonymous';
        }
        document.body.appendChild(script);
        // Create handler and store reference for cleanup.
        // We store the reference so we can remove this exact listener on cleanup.
        loadHandler = function () {
            if (callback) {
                callback();
            }
        };
        scriptElement = script;
        script.addEventListener('load', loadHandler);
    }
    else {
        // Script tag already exists in DOM (created by a previous component instance).
        // We need to check if it's actually finished loading or still in progress.
        if (window.Velt) {
            // Script has finished loading - window.Velt is available.
            // Trigger callback directly without attaching any event listener.
            if (callback) {
                callback();
            }
        }
        else {
            // IMPORTANT: Script tag exists but window.Velt is undefined.
            // This means the script is still loading (slow network scenario).
            // 
            // Previous bug: We used to trigger callback immediately here,
            // which caused initVelt to run with window.Velt = undefined.
            // 
            // Fix: Attach a load event listener to wait for the script to finish loading.
            // Using addEventListener (not onload) to avoid overwriting existing handlers.
            loadHandler = function () {
                if (callback) {
                    callback();
                }
            };
            scriptElement = existingScript;
            existingScript.addEventListener('load', loadHandler);
        }
    }
    // Return cleanup function to remove event listener when component unmounts.
    // This prevents the callback from firing on destroyed component instances,
    // which would cause React state updates on unmounted components.
    return function () {
        if (loadHandler && scriptElement) {
            scriptElement.removeEventListener('load', loadHandler);
        }
    };
};
export default loadVelt;
